import { generateFromObj } from '@bramus/pagination-sequence';
import { css } from 'astroturf';
import { graphql } from 'gatsby';
import React from 'react';
import { Feed } from 'react-bytesize-icons';
import PageHeader from '../components/PageHeader';
import PostListing from '../components/PostListing';
import SEO from '../components/SEO';
import Link from '../components/link';
import Layout from '../layout';
import NewsletterForm from '../components/newsletterForm';

const styles = css`
  .pagination {
    display: flex;
    justify-content: center;
    align-items: stretch;
    gap: 1rem;
    margin: 0 0 5rem;
    padding: 0;
    list-style: none;
    flex-wrap: wrap;

    & li {
      aspect-ratio: 1/1;

      &:is(:first-child, :last-child) {
        aspect-ratio: initial;
        & a {
          aspect-ratio: initial;
        }
      }
    }

    & li :is(a, span) {
      margin: 0;
      display: block;
      border-radius: 4px;
      background: rgba(16, 125, 181, 0.15);
      box-shadow: none;
      line-height: 1;
      transition: 0.25s ease-in-out color;
      padding: 1rem;
      white-space: nowrap;

      @media (min-width: 40em) {
        padding: 1.5rem;
      }

      &.active {
        background: none;
        color: #000;
        line-height: 2;
        padding: 1rem 0.5rem;
      }

      &:hover {
        color: #000;
      }
    }
    & li span {
      aspect-ratio: auto;
      height: 100%;
      line-height: 2;
      padding: 1rem 0.5rem;
      background: none;
      color: #000;
    }
  }

  .p {
    text-align: center;
    max-width: 40rem;
    margin: 0 auto 0rem;
    text-wrap: balance;
  }

  .subscribe {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin: 1rem 0 2rem;
    flex-wrap: wrap;
  }

  .subscribe-buttons {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .subscribe-buttons a {
    margin: 0;
    background: none;
    box-shadow: initial;
    border: 1px solid;
    padding: 0.5rem 1rem;
    border-radius: 40px;
    line-height: 1.5;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: 0.25s ease-in-out background;
    white-space: nowrap;

    &:hocus {
      background: rgb(0, 121, 177, 0.1);
    }
    &.rss {
      color: rgb(197, 77, 0);
      &:hocus {
        background: rgb(197, 77, 0, 0.1);
      }
    }

    & svg {
      vertical-align: middle;
    }
  }

  .paginate {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 1rem 0;
    gap: 1rem;

    span {
      margin: 0 auto;
    }
  }

  .prev,
  .next {
    width: 140px;
    display: none;
    @media (min-width: 40em) {
      display: block;
    }
  }
  .disabled {
    pointer-events: none;
    opacity: 0;
  }
`;

class Index extends React.Component {
  render() {
    const { currentPage, numPages } = this.props.pageContext;
    const isFirst = currentPage === 1;
    const isLast = currentPage === numPages;
    const prevPage = currentPage - 1 === 1 ? '/blog/' : `/blog/${(currentPage - 1).toString()}/`;
    const nextPage = `/blog/${(currentPage + 1).toString()}/`;

    const postEdges = this.props.data.allMdx.edges;

    const arr = generateFromObj({
      curPage: currentPage,
      numPages,
    });

    return (
      <Layout>
        <SEO title="Blog" keywords={['']} pathname={this.props.location.pathname} />
        <PageHeader>
          <h1>Blog</h1>
          <h2>The latest updates, web development tips and tricks, and in-depth articles about Polypane.</h2>

          <p className={styles.paginate}>
            <Link
              to={prevPage}
              rel="prev"
              className={[styles.prev, isFirst ? styles.disabled : ''].join(' ')}
              aria-hidden={isFirst}
            >
              ←&nbsp;Previous&nbsp;Page
            </Link>
            <span>
              Page {currentPage} of {numPages}
            </span>
            <Link
              to={nextPage}
              rel="next"
              className={[styles.next, isLast ? styles.disabled : ''].join(' ')}
              aria-hidden={isLast}
            >
              Next&nbsp;Page&nbsp;→
            </Link>
          </p>
        </PageHeader>

        <div className="index-container">
          <PostListing postEdges={postEdges} />
        </div>

        <p className={styles.p}>
          Stay up to date by subscribing to our newsletter, our RSS or by following us on social media.
        </p>
        <div className={styles.subscribe}>
          <div>
            <NewsletterForm />
          </div>
          <div className={styles.subscribeButtons}>
            <a href="/rss.xml" className={styles.rss}>
              <Feed width={16} height={16} color="#f60" strokeWidth={4} /> RSS
            </a>
            <a href="https://bsky.app/profile/polypane.app">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={16}
                height={16}
                aria-hidden="true"
                fill="dodgerblue"
              >
                <path d="M6.335 5.144c-1.654 -1.199 -4.335 -2.127 -4.335 .826c0 .59 .35 4.953 .556 5.661c.713 2.463 3.13 2.75 5.444 2.369c-4.045 .665 -4.889 3.208 -2.667 5.41c1.03 1.018 1.913 1.59 2.667 1.59c2 0 3.134 -2.769 3.5 -3.5c.333 -.667 .5 -1.167 .5 -1.5c0 .333 .167 .833 .5 1.5c.366 .731 1.5 3.5 3.5 3.5c.754 0 1.637 -.571 2.667 -1.59c2.222 -2.203 1.378 -4.746 -2.667 -5.41c2.314 .38 4.73 .094 5.444 -2.369c.206 -.708 .556 -5.072 .556 -5.661c0 -2.953 -2.68 -2.025 -4.335 -.826c-2.293 1.662 -4.76 5.048 -5.665 6.856c-.905 -1.808 -3.372 -5.194 -5.665 -6.856z" />
              </svg>{' '}
              @Polypane.app
            </a>
          </div>
        </div>

        <nav aria-label="Blog">
          <ul className={styles.pagination}>
            {!isFirst && (
              <li>
                <Link to={prevPage} rel="prev">
                  ← Previous Page
                </Link>
              </li>
            )}
            {arr.map((_, i) => (
              <li>
                {_ === '…' ? (
                  <span>{_}</span>
                ) : (
                  <Link
                    key={`pagination-number${i + 1}`}
                    to={`/blog/${i === 0 ? '' : `${i + 1}/`}`}
                    activeClassName={styles.active}
                  >
                    {_}
                  </Link>
                )}
              </li>
            ))}
            {!isLast && (
              <li>
                <Link to={nextPage} rel="next">
                  Next Page →
                </Link>
              </li>
            )}
          </ul>
        </nav>
      </Layout>
    );
  }
}

export default Index;

/* eslint no-undef: "off" */
export const blogListQuery = graphql`
  query blogListQuery($skip: Int!, $limit: Int!) {
    allMdx(
      filter: { fields: { collection: { eq: "posts" } } }
      limit: $limit
      skip: $skip
      sort: { fields: [fields___updated, fields___date], order: DESC }
    ) {
      edges {
        node {
          fields {
            slug
            date
            updated
            collection
          }
          excerpt(pruneLength: 220)
          timeToRead
          frontmatter {
            title
            cover
            date
            updated
          }
        }
      }
    }
  }
`;
